// src/worker/scan.js - Scan (v133.118), the pure half: the product search, the figures for one product,
// the rows an upload may write, and the AI prompts. No fetch, no env: everything here is tested directly.
//
// Matt, 14 Oct 2026: "the store could just take a pic in the bay - AI then looks at the description and
// then using RAG tried to find it. if it cant it asks to look at the barcode. (There will be a bit of a
// cheat - if the shelf edge is in the photo it carries the system description)". The page is /scan.
//
// THE DATA is the DSR SKU sales report (one per country, UAE and Qatar), uploaded by Head Office in
// admin (Top 50, Scan data). Its "data" sheet is one line per item on a bill. The browser turns it into
// three kinds of row before anything is sent (src/scanAdmin/parse.js), and this file decides which
// fields of those rows the Worker will write:
//   scan_products  one row per product per country: the system description, hierarchy, class, price,
//                  warehouse stock, average cost
//   scan_stock     one row per product per branch: a store's OWN stock. Empty from the DSR file, whose
//                  "cubes store soh" is the total for all stores (the same on every store's line), kept
//                  on scan_products.stores_soh instead; filled when a per-store stock report comes
//   scan_daily     one row per product per branch per day: units, sales without VAT, cost, discount
// NEVER the bill number, the bill remarks (loyalty and delivery-order numbers: they identify customers)
// or the discount reason. Matt's decision, 14 Oct 2026: cost and margin are for Head Office only.
//
// THE CATALOGUE (Matt, 14 Oct 2026: "would the attached files also help?" - "yes build it"): the Cubes
// "Product details main barcode" file (every product, sold or not, with its main barcode, status,
// standard price and cost) and the "Price Matrix - linked barcode" file (every barcode linked to a
// product, and its current price in the branch it was exported from). They make every product findable
// (not just those that sold in the DSR period) and let a barcode find its product directly:
//   scan_products  + status, standard_price, standard_cost (HO only), shelf_price (Price 1)
//   scan_barcodes  one row per barcode per country -> its product (main or linked)
// Each upload kind writes ONLY its own columns of scan_products (a PostgREST upsert updates the columns
// it is sent), so a catalogue upload never clears the DSR's sales fields, and the DSR never clears status.
//
// THE SEARCH ("RAG"). The AI reads the photo first (the shelf-edge label above all, then the pack), then
// rankCandidates() finds the closest products in that country's list, then the AI chooses among those
// candidates only. It can never name a product that is not in the list. About 2,500 products per
// country, so a plain token and trigram score in the Worker does the retrieval: no vector store.

export const SCAN_COUNTRIES = ['UAE', 'QATAR'];
// One model constant for every Scan call (the claude-api default). Haiku would be far cheaper per scan:
// changing this line is the whole switch.
export const SCAN_MODEL = 'claude-opus-5-5';
export const SCAN_MAX_IMAGE_CHARS = 2000000;   // a base64 JPEG the page has already shrunk to 1280px
export const SCAN_CANDIDATES = 15;
export const SCAN_UPLOAD_MAX_ROWS = 2000;      // per request; the page sends the file in chunks

// ── small helpers ────────────────────────────────────────────────────────────────────────────────
// v136.51: stores.country is written "ELC - Qatar" / "ELC - UAE" (and older rows "Qatar", "Dubai"); the exact
// words alone missed every one, so every Doha store was read as UAE, with UAE data and AED (reported 26 Oct).
export function normCountry(v) {
  const s = String(v == null ? '' : v).trim().toUpperCase();
  if (s === 'UAE' || s === 'UNITED ARAB EMIRATES' || s === 'AE') return 'UAE';
  if (s === 'QATAR' || s === 'QA' || s === 'QAT') return 'QATAR';
  if (/\bQATAR\b|\bDOHA\b/.test(s)) return 'QATAR';
  if (/\bUAE\b|\bEMIRATES\b|\bDUBAI\b|\bABU DHABI\b|\bSHARJAH\b|\bAL AIN\b|\bAJMAN\b|\bFUJAIRAH\b|\bRAS AL KHAIMAH\b/.test(s)) return 'UAE';
  return null;
}
// A store's Scan country: its country column, else its name (the Qatar stores are the "Doha - ..." ones).
export function storeCountry(st) {
  if (!st) return null;
  return normCountry(st.country) || (/^\s*DOHA\b/i.test(String(st.name || '')) ? 'QATAR' : null);
}
export function normCode(v) {
  return String(v == null ? '' : v).trim().toUpperCase().replace(/\s+/g, '');
}
// A barcode as the stock system or a scanner gives it -> one canonical form, or null. The files carry a
// few hundred oddities: a no-break space in front ("\u00a0000772037853"), a suffix ("193052040299-D",
// "817889011849."), two codes in one cell ("011543820116,0115438") and dummies ("0", "0000", "0.0").
// The leading run of digits is the barcode; leading zeros are dropped so a 12-digit UPC and the same
// code scanned as a 13-digit EAN ("0" in front) meet; under 8 digits, or all zeros, is no barcode.
export function normBarcode(v) {
  const m = String(v == null ? '' : v).replace(/[\s\u00a0]+/g, '').match(/^\d+/);
  if (!m || m[0].length < 8 || m[0].length > 14) return null;
  const b = m[0].replace(/^0+/, '');
  return b.length >= 6 ? b : null;
}

// Upper case, letters and digits only, single spaces. Product names in the system are upper case
// abbreviations ("DPRINCESS DOLL CINDIRELA BIRTHDAY FSHION"), so this is the form both sides meet in.
export function normText(v) {
  return String(v == null ? '' : v).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}
export function tokens(v) {
  const t = normText(v);
  return t ? t.split(' ') : [];
}
function trigrams(s) {
  const p = '  ' + s + ' ';
  const out = new Set();
  for (let i = 0; i < p.length - 2; i++) out.add(p.slice(i, i + 3));
  return out;
}
// Dice coefficient over character trigrams (0 to 1). Copes with the system's spellings
// ("CINDIRELA" against "CINDERELLA") and joined words ("TOYSTORY5" against "TOY STORY 5").
export function trigramSim(a, b) {
  const x = normText(a), y = normText(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  const A = trigrams(x), B = trigrams(y);
  let both = 0;
  A.forEach((g) => { if (B.has(g)) both++; });
  return (2 * both) / (A.size + B.size);
}
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const firstNum = (...vs) => { for (const v of vs) { if (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))) return Number(v); } return null; };
const round = (v, dp) => { const f = Math.pow(10, dp || 0); return Math.round(num(v) * f) / f; };

// ── the search ───────────────────────────────────────────────────────────────────────────────────
// What the AI read from the photo, tidied: every string bounded, codes upper case.
export function cleanExtract(x) {
  const o = (x && typeof x === 'object') ? x : {};
  const str = (v, n) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, n || 200);
  const list = (v, n) => (Array.isArray(v) ? v : []).map((s) => str(s, 80)).filter(Boolean).slice(0, n || 10);
  const money = (v) => { const m = String(v == null ? '' : v).replace(/,/g, '').match(/\d+(\.\d+)?/); return m ? Number(m[0]) : null; };
  const items = (Array.isArray(o.items) ? o.items : []).slice(0, 40).map((it) => {
    const i = (it && typeof it === 'object') ? it : {};
    const f = parseInt(i.facings, 10);
    return {
      label_text: str(i.label_text, 200),
      product_code: normCode(String(i.product_code == null ? '' : i.product_code).replace(/\(.*$/, '')).slice(0, 40),
      label_price: money(i.label_price),
      was_price: money(i.was_price),
      pack_name: str(i.pack_name, 160),
      brand: str(i.brand, 80),
      facings: Number.isFinite(f) && f > 0 ? Math.min(f, 50) : 1,
      barcode_digits: str(i.barcode_digits, 20).replace(/\D/g, ''),
    };
  }).filter((i) => i.label_text || i.product_code || i.pack_name || i.barcode_digits);
  return {
    readable: o.readable !== false,
    scene: o.scene === 'bay' ? 'bay' : 'product',
    range_name: str(o.range_name, 80),
    shelf_label_text: str(o.shelf_label_text, 300),
    product_name: str(o.product_name, 200),
    brand: str(o.brand, 80),
    product_codes: list(o.product_codes, 6).map((c) => normCode(c.replace(/\(.*$/, ''))).filter(Boolean),
    search_terms: list(o.search_terms, 10),
    barcode_digits: str(o.barcode_digits, 20).replace(/\D/g, ''),
    items,
  };
}

// Words that say nothing about WHICH product it is.
const STOP = new Set(['THE', 'AND', 'FOR', 'WITH', 'OF', 'IN', 'TO', 'A', 'AN', 'NEW', 'TOY', 'TOYS', 'AED', 'QAR', 'PCS', 'PC', 'EACH', 'NOW', 'WAS', 'ONLY', 'SALE', 'PRICE', 'OFF']);

function queryTokens(ex) {
  const all = [].concat(tokens(ex.shelf_label_text), tokens(ex.product_name), tokens(ex.brand), ...ex.search_terms.map(tokens));
  const out = [];
  all.forEach((t) => {
    if (t.length < 2 || STOP.has(t) || /^\d{1,2}$/.test(t)) return;
    if (out.indexOf(t) === -1) out.push(t);
  });
  return out.slice(0, 40);
}

// The list's index, built once per list (the Worker keeps the list for ten minutes): each product's
// words, how many products use each word, and each word's trigrams. Scoring a photo then compares the
// words read with each DISTINCT word in the list once, not with every product's words.
const _index = new WeakMap();
function indexOf(list) {
  let ix = _index.get(list);
  if (ix) return ix;
  const df = new Map();
  const docs = list.map((p) => {
    const toks = Array.from(new Set(tokens([p.description, p.brand, p.sub_brand].join(' '))));
    toks.forEach((t) => df.set(t, (df.get(t) || 0) + 1));
    return toks;
  });
  const tri = new Map();
  df.forEach((_, t) => { if (t.length >= 4) tri.set(t, trigrams(t)); });
  ix = { docs, df, tri };
  _index.set(list, ix);
  return ix;
}

function tokenMatch(q, qTri, p, pTri) {
  if (q === p) return 1;
  if (q.length >= 3 && p.length >= 3 && (p.indexOf(q) === 0 || q.indexOf(p) === 0)) return 0.75;
  if (qTri && pTri) {
    let both = 0;
    qTri.forEach((g) => { if (pTri.has(g)) both++; });
    const s = (2 * both) / (qTri.size + pTri.size);
    if (s >= 0.55) return s * 0.8;
  }
  return 0;
}

// products: [{ prod_code, description, brand, sub_brand, ... }]. Returns the best `limit` as
// [{ product, score, why }], best first. Pure.
export function rankCandidates(products, extract, limit) {
  const ex = cleanExtract(extract);
  const list = Array.isArray(products) ? products : [];
  const max = limit || SCAN_CANDIDATES;
  const codes = new Set(ex.product_codes.filter((c) => c.length >= 3));
  const qt = queryTokens(ex);
  const label = normText(ex.shelf_label_text);
  if (!qt.length && !codes.size) return [];

  const { docs, df, tri } = indexOf(list);
  // How common each word is across the list: "HOTWHEELS" on 300 products says less than "CARWASH".
  const weight = (t) => 1 / Math.log(2 + (df.get(t) || 0));
  // Each word read, against each distinct word in the list, once.
  const best = qt.map((q) => {
    const qTri = q.length >= 4 ? trigrams(q) : null;
    const m = new Map();
    df.forEach((_, t) => { const v = tokenMatch(q, qTri, t, tri.get(t) || null); if (v > 0) m.set(t, v); });
    return { q, w: weight(q), m };
  });
  const totalW = best.reduce((s, b) => s + b.w, 0);
  const labelTri = label ? trigrams(label) : null;

  const scored = [];
  list.forEach((p, i) => {
    const code = normCode(p.prod_code);
    let score = 0;
    const why = [];
    if (code && codes.has(code)) { score += 10; why.push('code'); }
    // a code printed inside the label text ("ITEM: HDP05")
    else if (code && code.length >= 4 && qt.indexOf(code) !== -1) { score += 10; why.push('code'); }
    if (qt.length) {
      const ptoks = docs[i];
      let got = 0;
      const used = new Set();
      best.forEach((b) => {
        let top = 0, topTok = null;
        for (const t of ptoks) { const v = b.m.get(t); if (v && v > top) { top = v; topTok = t; } }
        if (top > 0) { got += top * b.w; used.add(topTok); }
      });
      if (!used.size && !why.length) return;
      const qCover = totalW ? got / totalW : 0;                     // how much of what was read this product explains
      const pCover = ptoks.length ? used.size / ptoks.length : 0;   // how much of this product was read
      score += 0.55 * qCover + 0.45 * pCover;
      if (labelTri) {
        // The shelf-edge "cheat": the label carries the system description, so a near-copy wins.
        const d = normText(p.description);
        let s = 0;
        if (d) {
          if (d === label) s = 1;
          else { const dTri = trigrams(d); let both = 0; labelTri.forEach((g) => { if (dTri.has(g)) both++; }); s = (2 * both) / (labelTri.size + dTri.size); }
        }
        score += 0.6 * s;
        if (s >= 0.8) why.push('label');
      }
    }
    if (score > 0.15) scored.push({ product: p, score: round(score, 4), why });
  });
  scored.sort((a, b) => b.score - a.score || String(a.product.prod_code).localeCompare(String(b.product.prod_code)));
  return scored.slice(0, max);
}

// Decide without a second AI call when the answer is plain: a code that is in the list, or a label
// that is a near-copy of one description and clearly ahead of the next.
export function obviousMatch(ranked) {
  if (!Array.isArray(ranked) || !ranked.length) return null;
  const top = ranked[0];
  if (top.why.indexOf('code') !== -1) return top.product;
  const next = ranked[1];
  if (top.why.indexOf('label') !== -1 && (!next || top.score - next.score >= 0.25)) return top.product;
  return null;
}

// ── the AI prompts (both answer in JSON, through output_config.format) ──────────────────────────
const ITEM_SCHEMA = {
  type: 'object',
  properties: {
    label_text: { type: 'string' },
    product_code: { type: 'string' },
    label_price: { type: 'string' },
    was_price: { type: 'string' },
    pack_name: { type: 'string' },
    brand: { type: 'string' },
    facings: { type: 'integer' },
    barcode_digits: { type: 'string' },
  },
  required: ['label_text', 'product_code', 'label_price', 'was_price', 'pack_name', 'brand', 'facings', 'barcode_digits'],
  additionalProperties: false,
};
export const EXTRACT_SCHEMA = {
  type: 'object',
  properties: {
    readable: { type: 'boolean' },
    scene: { type: 'string', enum: ['product', 'bay'] },
    range_name: { type: 'string' },
    shelf_label_text: { type: 'string' },
    product_name: { type: 'string' },
    brand: { type: 'string' },
    product_codes: { type: 'array', items: { type: 'string' } },
    search_terms: { type: 'array', items: { type: 'string' } },
    barcode_digits: { type: 'string' },
    items: { type: 'array', items: ITEM_SCHEMA },
  },
  required: ['readable', 'scene', 'range_name', 'shelf_label_text', 'product_name', 'brand', 'product_codes', 'search_terms', 'barcode_digits', 'items'],
  additionalProperties: false,
};

// Matt, 14 Oct 2026 (with photos of a Toy Story gondola, a Pizza Planet pack, a Dora clearance label and
// a VTech bay): "if its looking at a bay e.g. toy story - how is that entire range doing? right price?
// right facings? which items aren't performing?" So one photo is read either as ONE product (the
// fields at the top level) or as a BAY (scene "bay": every different product in `items`). The shelf-edge
// labels in those photos print the description, the price ("WAS 35 / 20" on clearance), and the product
// code with the store class in brackets ("HWR88 (100)", "6071556 (CLR-FDD)").
export function buildExtractPrompt() {
  return [
    'You read photos taken by staff in Early Learning Centre and The Entertainer toy stores in the UAE and Qatar, so the store can look products up in its own stock system.',
    'First decide the scene. "product": the photo is mainly ONE product (a close-up, or one pack with its label). "bay": a shelf, gondola or bay with several DIFFERENT products.',
    'Shelf-edge labels matter most. Each prints the stock system\'s own description in upper case, often abbreviated (for example "TOYSTORYS PIZZA PLANET ADVENTURE PACK"), the price, sometimes a "WAS" price, and a product code with the store class in brackets (for example "HWR88 (100)" or "6071556 (CLR-FDD)"). Copy label text exactly as printed, mistakes and all. A product code is the part BEFORE the brackets. Never invent a code, price or digit you cannot read: leave it empty.',
    'For scene "product": put the label text in shelf_label_text, any code in product_codes, the brand and the name as printed on the pack in brand and product_name, up to 8 short identifying words (character, size, colour, pack count) in search_terms, and the digits under a barcode, if readable, in barcode_digits. Leave items empty.',
    'For scene "bay": put the range or licence the bay is for (for example "Toy Story", "VTech") in range_name, and list EVERY different product you can see in items, one entry per product (not per unit), at most 40: its label_text, product_code, label_price and was_price as printed (digits only, for example "159"), pack_name and brand as printed on the pack, facings = how many units of it face the front side by side across all shelves (your best count, 1 if one), and barcode_digits if readable. Match each label to the product directly above it. Also fill the top-level fields for the product in the middle of the photo.',
    'Describe products only. Never describe a person in the photo. If you cannot read anything useful, set readable to false and leave the rest empty.',
  ].join('\n');
}

export const CHOOSE_SCHEMA = {
  type: 'object',
  properties: {
    prod_code: { type: 'string' },
    confidence: { type: 'string', enum: ['high', 'medium', 'low', 'none'] },
    reason: { type: 'string' },
  },
  required: ['prod_code', 'confidence', 'reason'],
  additionalProperties: false,
};

// The second call: choose among the candidates only. The candidate list is the stock system's own data.
export function buildChoosePrompt(extract, ranked) {
  const ex = cleanExtract(extract);
  const system = [
    'You match what was read from a photo of a toy in a store bay to ONE product in the store\'s stock list.',
    'Choose only from the candidates given. If none of them is clearly the same product, answer confidence "none" and prod_code "".',
    'confidence: "high" when the description, brand and details agree; "medium" when it is very likely but a detail (size, colour, version) cannot be confirmed; "low" when it is a guess.',
    'reason: one short sentence a store team member would understand.',
  ].join('\n');
  const lines = (ranked || []).map((r) => {
    const p = r.product;
    return '- ' + p.prod_code + ' | ' + (p.description || '') + ' | brand ' + (p.brand || '') + (p.sub_brand ? ' / ' + p.sub_brand : '') + (p.sub_segment ? ' | ' + p.sub_segment : '');
  });
  const user = 'What was read:\n' +
    'shelf label: ' + (ex.shelf_label_text || '(none)') + '\n' +
    'pack name: ' + (ex.product_name || '(none)') + '\n' +
    'brand: ' + (ex.brand || '(none)') + '\n' +
    'codes: ' + (ex.product_codes.join(', ') || '(none)') + '\n' +
    'other words: ' + (ex.search_terms.join(', ') || '(none)') + '\n\n' +
    'Candidates (code | description | brand | segment):\n' + lines.join('\n');
  return { system, user };
}

// ── the figures for one product ──────────────────────────────────────────────────────────────────
// product: the scan_products row. stock: scan_stock rows for it (every branch). daily: scan_daily rows
// for it. branch: this viewer's branch name as the file spells it (or null). period: { start, end }
// of the sales window (v136.20: salesWindow, the last 28 days held). headOffice: true only for Head Office (Matt: cost and margin for HO only).
// v136.16: totalsOnly = the latest sales file is a weekly total (the TEAL report has no date column), so
// there is no day-by-day line and no "days sold". stockAsOf = the day of the stores' own stock file
// (Cubes, every store): once it is loaded, a store with no stock row has none (the upload sends only
// the lines that are not zero). Only daily rows inside the period count, so an older file's days
// never inflate the latest period's rate of sale.
// v136.20: THE SALES WINDOW. Daily sales files arrive from 21 Oct 2026, so "the newest upload's period" would
// narrow Scan to one day. The period is a ROLLING 28 DAYS per country instead: it ends on the newest sales day
// held (the latest period_end of the country's uploads) and starts 27 days before, or on the earliest day held
// when there is less history. uploads: the country's scan_uploads rows (id, period_start, period_end,
// uploaded_at, skus, branches, totals_only). The window is weekly totals (no day-by-day line, no days sold) when
// a weekly upload (totals_only) overlaps it and its days were not loaded again later by daily files.
// -> { start, end, days, totals_only, key, uploaded_at, skus, branches } or null.
export const SALES_WINDOW_DAYS = 28;
const dayAdd = (iso, n) => new Date(Date.parse(iso + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
// v136.31: `want` ({ start, end }, optional) is a period the viewer chose; it is kept inside the days held. Without
// it the window is the rolling 28 days as before. held_from / held_to: the first and last day of sales held.
export function salesWindow(uploads, want) {
  const ok = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || '').slice(0, 10));
  const ups = (Array.isArray(uploads) ? uploads : []).filter((u) => u && ok(u.period_start) && ok(u.period_end))
    .map((u) => Object.assign({}, u, { period_start: String(u.period_start).slice(0, 10), period_end: String(u.period_end).slice(0, 10) }));
  if (!ups.length) return null;
  const heldTo = ups.reduce((m, u) => (u.period_end > m ? u.period_end : m), ups[0].period_end);
  const earliest = ups.reduce((m, u) => (u.period_start < m ? u.period_start : m), ups[0].period_start);
  const chosen = want && ok(want.start) && ok(want.end) && want.start <= want.end;
  const end = chosen && want.end < heldTo ? want.end : heldTo;
  const from = chosen ? want.start : dayAdd(end, -(SALES_WINDOW_DAYS - 1));
  const start = earliest > from ? earliest : from;
  if (start > end) return null;
  const inside = ups.filter((u) => u.period_end >= start && u.period_start <= end);
  // a weekly upload counts only while no later daily upload has loaded its days again
  const superseded = (w) => {
    const later = inside.filter((u) => !u.totals_only && Number(u.id) > Number(w.id));
    for (let d = w.period_start < start ? start : w.period_start; d <= w.period_end && d <= end; d = dayAdd(d, 1)) {
      if (!later.some((u) => u.period_start <= d && u.period_end >= d)) return false;
    }
    return true;
  };
  const totalsOnly = inside.some((u) => u.totals_only && !superseded(u));
  const newest = inside.reduce((m, u) => (Number(u.id) > Number(m.id) ? u : m), inside[0]);
  const branches = new Set();
  inside.forEach((u) => {
    let b = u.branches;
    if (typeof b === 'string') { try { b = JSON.parse(b); } catch (e) { b = null; } }
    (Array.isArray(b) ? b : []).forEach((x) => branches.add(String(x)));
  });
  return {
    start, end, days: Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1,
    totals_only: totalsOnly,
    key: start + '~' + end + ':' + inside.map((u) => u.id).sort().join('.'),   // changes whenever the window or a file inside it changes
    uploaded_at: (newest && newest.uploaded_at) || null,   // v136.31: a chosen period can fall in a gap with no file
    skus: inside.reduce((m, u) => Math.max(m, Number(u.skus) || 0), 0),
    branches: Array.from(branches),
    held_from: earliest, held_to: heldTo,   // v136.31
  };
}

export function inPeriod(day, period) {
  const d = String(day || '').slice(0, 10);
  if (!period || !period.start || !period.end) return !!d;
  return d >= period.start && d <= period.end;
}
// v136.20: stockAt = when the store stock was uploaded (ISO), for a file whose name carries no date (the stock
// is as at the upload). priceKnown / onOrderKnown = the store stock file carried each store's price / what is
// on order (the new daily files); without them a store's price and on-order stay unknown (null), never zero. A store
// with no line in such a file charges the usual price (the product's shelf_price, written by the same upload).
export function buildProductStats({ product, stock, daily, branch, period, headOffice, totalsOnly, stockAsOf, stockAt, priceKnown, onOrderKnown }) {
  const p = product || {};
  const per = new Map();   // branch -> { qty, sales, cost, soh, days:Set }
  const get = (b) => { if (!per.has(b)) per.set(b, { branch: b, qty: 0, sales: 0, cost: 0, soh: null, days: new Set() }); return per.get(b); };
  const lineOf = new Map();   // branch -> its store stock line
  (stock || []).forEach((s) => { if (s && s.branch) lineOf.set(s.branch, s); });
  const usual = priceKnown && p.shelf_price != null && isFinite(Number(p.shelf_price)) ? Number(p.shelf_price) : null;
  const priceAt = (b) => { const s = lineOf.get(b); return s && s.store_price != null && isFinite(Number(s.store_price)) ? Number(s.store_price) : usual; };
  const orderAt = (b) => { const s = lineOf.get(b); return s && s.on_order != null && num(s.on_order) !== 0 ? num(s.on_order) : (onOrderKnown ? 0 : null); };
  const orderDateAt = (b) => { const s = lineOf.get(b); return s && s.on_order_date && num(s.on_order) !== 0 ? String(s.on_order_date).slice(0, 10) : null; };
  daily = (daily || []).filter((d) => d && inPeriod(d.sale_date, period));
  const dates = new Set();
  daily.forEach((d) => {
    if (!d || !d.branch || !d.sale_date) return;
    const r = get(d.branch);
    r.qty += num(d.qty); r.sales += num(d.sales); r.cost += num(d.cost);
    if (num(d.qty) > 0) r.days.add(d.sale_date);
    dates.add(String(d.sale_date).slice(0, 10));
  });
  // a store's own stock: on the stores that sold it, and on any other store that holds some
  (stock || []).forEach((s) => {
    if (!s || !s.branch) return;
    const q = num(s.store_soh);
    // v136.20: and on any store with something on order
    if (per.has(s.branch) || q !== 0 || (s.on_order != null && num(s.on_order) !== 0)) get(s.branch).soh = q;
  });
  if (stockAsOf) per.forEach((r) => { if (r.soh == null) r.soh = 0; });
  const start = (period && period.start) || Array.from(dates).sort()[0] || null;
  const end = (period && period.end) || Array.from(dates).sort().slice(-1)[0] || null;
  const days = start && end ? Math.max(1, Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1) : Math.max(1, dates.size);

  const cover = (soh, qty) => {
    if (soh == null) return null;
    const perDay = qty / days;
    if (perDay <= 0) return soh > 0 ? null : 0;   // null = stock and no sales in the period: no rate to divide by
    return round(soh / perDay / 7, 1);
  };
  const stores = Array.from(per.values()).map((r) => ({
    branch: r.branch,
    qty: round(r.qty, 0),
    sales: round(r.sales, 2),
    soh: r.soh,
    days_sold: totalsOnly ? null : r.days.size,
    weeks_cover: cover(r.soh, r.qty),
    ...((priceKnown || onOrderKnown) ? { price: priceAt(r.branch), on_order: orderAt(r.branch), on_order_date: orderDateAt(r.branch) } : {}),
    ...(headOffice ? { cost: round(r.cost, 2), margin_pct: r.sales > 0 ? round((r.sales - r.cost) / r.sales * 100, 1) : null } : {}),
  })).sort((a, b) => b.qty - a.qty || b.sales - a.sales || a.branch.localeCompare(b.branch));

  const n = stores.length;
  const totQty = stores.reduce((s, r) => s + r.qty, 0);
  const totSales = stores.reduce((s, r) => s + r.sales, 0);
  // Stock in all stores: the stores' own figures when there are any, else the file's all-stores total.
  const haveStoreSoh = !!stockAsOf || stores.some((r) => r.soh != null);
  const totSoh = haveStoreSoh ? stores.reduce((s, r) => s + (r.soh == null ? 0 : r.soh), 0) : (p.stores_soh == null ? null : num(p.stores_soh));
  const avgQty = n ? totQty / n : 0;
  const mine = branch ? stores.find((r) => r.branch === branch) || null : null;
  const rank = mine ? stores.indexOf(mine) + 1 : null;

  // The daily line: this store, and the average store that carries it.
  const dayList = [];
  if (start && end) {
    for (let t = Date.parse(start); t <= Date.parse(end); t += 86400000) dayList.push(new Date(t).toISOString().slice(0, 10));
  }
  const byDay = new Map(dayList.map((d) => [d, { date: d, mine: 0, all: 0 }]));
  daily.forEach((d) => {
    const k = String(d && d.sale_date || '').slice(0, 10);
    const row = byDay.get(k);
    if (!row) return;
    row.all += num(d.qty);
    if (branch && d.branch === branch) row.mine += num(d.qty);
  });
  const series = totalsOnly ? [] : dayList.map((d) => { const r = byDay.get(d); return { date: d, mine: round(r.mine, 0), avg: n ? round(r.all / n, 2) : 0 }; });

  const out = {
    product: {
      prod_code: p.prod_code, description: p.description, brand: p.brand, sub_brand: p.sub_brand,
      main_group: p.main_group, sub_group: p.sub_group, sub_segment: p.sub_segment,
      class_store: p.class_store, class_label: classLabel(p.class_store),
      // The price on the shelf: the price matrix's Price 1 when loaded, else the till price in the
      // sales file, else the catalogue's standard price.
      price: firstNum(p.shelf_price, p.selling_price, p.standard_price),
      status: p.status || null,
      selling_price: p.selling_price == null ? null : num(p.selling_price),
      wh_soh: p.wh_soh == null ? null : num(p.wh_soh),
      stores_soh: totSoh == null ? null : round(totSoh, 0),
      created_on: p.created_on || null, last_sold: p.last_sold || null,
      // v136.20: on order in all stores, from the new stock file (null = no file has said: never shown as zero)
      on_order: onOrderKnown ? round(Array.from(lineOf.values()).reduce((s, l) => s + (l.on_order == null ? 0 : num(l.on_order)), 0), 0) : null,
      ...(headOffice ? {
        avg_cost: p.avg_cost == null ? null : num(p.avg_cost),
        standard_cost: p.standard_cost == null ? null : num(p.standard_cost),
        margin_pct: totSales > 0 ? round((totSales - stores.reduce((s, r) => s + (r.cost || 0), 0)) / totSales * 100, 1) : null,
      } : {}),
    },
    period: { start, end, days, totals_only: !!totalsOnly },
    country: { stores: n, qty: round(totQty, 0), sales: round(totSales, 2), soh: totSoh == null ? null : round(totSoh, 0), weeks_cover: totSoh == null ? null : cover(totSoh, totQty), avg_qty: round(avgQty, 1), store_soh_known: haveStoreSoh, stock_as_of: stockAsOf || null, stock_at: stockAt || null },
    mine: mine ? {
      ...mine,
      rank, of: n,
      vs_avg_pct: avgQty > 0 ? round((mine.qty - avgQty) / avgQty * 100, 0) : null,
    } : (branch ? { branch, qty: 0, sales: 0, soh: null, days_sold: 0, weeks_cover: null, rank: null, of: n, vs_avg_pct: avgQty > 0 ? -100 : null, not_in_file: true,
      ...((priceKnown || onOrderKnown) ? { price: priceAt(branch), on_order: orderAt(branch), on_order_date: orderDateAt(branch) } : {}) } : null),
    stores,
    series,
  };
  return out;
}

// The store classes in the file. Only the ones whose meaning is plain from the code are labelled;
// 100 to 107, OOS, INOUT and ECOM show as the code until Matt confirms what each means.
export function classLabel(code) {
  const c = String(code == null ? '' : code).trim().toUpperCase();
  if (!c) return '';
  if (c === 'EOL') return 'End of line';
  if (c === 'CONSO') return 'Consumable';
  if (c === 'RTV') return 'Return to vendor';
  if (c === 'CLR' || c.indexOf('CLR-') === 0) return 'Clearance';
  if (c.indexOf('PROMO') === 0) return 'Promotion';
  return '';
}

// ── the advice ───────────────────────────────────────────────────────────────────────────────────
export const ADVICE_SCHEMA = {
  type: 'object',
  properties: {
    headline: { type: 'string' },
    actions: { type: 'array', items: { type: 'string' } },
  },
  required: ['headline', 'actions'],
  additionalProperties: false,
};

// What the AI is told. Built from the SAME stats the viewer was sent, so a non-Head Office viewer's
// advice can never be based on (or repeat) cost or margin: those fields are not in their stats at all.
export function buildAdvicePrompt(stats, storeName) {
  const s = stats || {};
  const p = s.product || {};
  const system = [
    'You advise the team in one Early Learning Centre or The Entertainer toy store in the UAE or Qatar about ONE product, from its sales and stock figures.',
    'Give a one-line headline and 2 to 4 short, practical actions the store team can do this week (for example: move it to a better position or the front of the bay, check the stock room and refill the bay, ask the area manager for a transfer from a store with more cover, flag it to Head Office for a reorder, push it in conversations with customers, or reduce the facings for a slow line).',
    'Use only the figures given. Do not invent figures, prices, promotions or dates. Say "the average store" for the comparison. Weeks of cover = stock divided by its weekly rate of sale.',
    'Plain English, no jargon, no markdown, each action at most 25 words.',
    p.margin_pct != null ? 'You may mention the margin, because this reader is Head Office.' : 'Do not talk about cost, profit or margin.',
  ].join('\n');
  const mine = s.mine;
  const lines = [
    'Product: ' + (p.description || '') + ' (' + (p.prod_code || '') + '), brand ' + (p.brand || '') + (p.sub_brand ? ' / ' + p.sub_brand : ''),
    'Range: ' + [p.main_group, p.sub_group, p.sub_segment].filter(Boolean).join(' > '),
    'Store class: ' + (p.class_store || 'unknown') + (p.class_label ? ' (' + p.class_label + ')' : ''),
    'Price: ' + (p.price == null ? 'unknown' : p.price) + (p.status ? ' | system status ' + p.status : ''),
    (s.country && s.country.stores === 0) ? 'NO store sold it in this period.' : '',
    'Warehouse stock: ' + (p.wh_soh == null ? 'unknown' : p.wh_soh),
    'Period: ' + (s.period && s.period.start) + ' to ' + (s.period && s.period.end) + ' (' + (s.period && s.period.days) + ' days)',
    'All stores that sold it: ' + (s.country && s.country.stores) + ' stores, ' + (s.country && s.country.qty) + ' units sold, average ' + (s.country && s.country.avg_qty) + ' per store',
    'Stock in all stores together: ' + (s.country && s.country.soh != null ? s.country.soh + ' units, ' + (s.country.weeks_cover == null ? 'cover n/a' : s.country.weeks_cover + ' weeks of cover across the stores') : 'unknown') +
      (s.country && s.country.store_soh_known ? '' : '. Each store\'s OWN stock is not in the data: never state a stock figure for this store; suggest checking the bay and the stock room instead.'),
  ];
  if (mine) {
    lines.push('This store (' + (storeName || mine.branch) + '): ' + mine.qty + ' units sold, sales ' + mine.sales + ', stock ' + (mine.soh == null ? 'not in the file' : mine.soh) +
      ', weeks of cover ' + (mine.weeks_cover == null ? 'n/a' : mine.weeks_cover) + ', rank ' + (mine.rank == null ? 'n/a' : mine.rank + ' of ' + mine.of) +
      ', versus the average store ' + (mine.vs_avg_pct == null ? 'n/a' : (mine.vs_avg_pct >= 0 ? '+' : '') + mine.vs_avg_pct + '%') + (mine.not_in_file ? ' (no sales or stock line for this store in the file)' : ''));
  } else {
    lines.push('No single store is selected: advise Head Office or an area manager on the product across stores.');
  }
  const top = (s.stores || []).slice(0, 5).map((r) => r.branch + ' ' + r.qty + ' sold' + (r.soh == null ? '' : ', stock ' + r.soh)).join('; ');
  if (top) lines.push('Best stores: ' + top);
  const heavy = (s.stores || []).filter((r) => r.weeks_cover != null && r.weeks_cover >= 12).slice(0, 5).map((r) => r.branch + ' (' + r.weeks_cover + ' weeks)').join('; ');
  if (heavy) lines.push('Stores with a lot of cover: ' + heavy);
  if (p.margin_pct != null) lines.push('Margin: ' + p.margin_pct + '%');
  return { system, user: lines.filter(Boolean).join('\n') };
}

export function cleanAdvice(x) {
  const o = (x && typeof x === 'object') ? x : {};
  const strip = (v, n) => String(v == null ? '' : v).replace(/[<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, n);
  const actions = (Array.isArray(o.actions) ? o.actions : []).map((a) => strip(a, 220)).filter(Boolean).slice(0, 5);
  const headline = strip(o.headline, 200);
  return headline || actions.length ? { headline, actions } : null;
}

// ── what an upload may write (an allow-list per table; anything else in a row is dropped) ─────────
const dateOk = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || '')) ? String(v) : null;
const txt = (v, n) => { const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim(); return s ? s.slice(0, n) : null; };
const numOrNull = (v) => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

export function cleanUploadRows(kind, rows, country) {
  const c = normCountry(country);
  if (!c) return { ok: false, error: 'country must be UAE or Qatar' };
  if (!SCAN_TABLE[kind]) return { ok: false, error: 'unknown kind of rows' };
  if (!Array.isArray(rows)) return { ok: false, error: 'rows must be a list' };
  if (rows.length > SCAN_UPLOAD_MAX_ROWS) return { ok: false, error: 'at most ' + SCAN_UPLOAD_MAX_ROWS + ' rows at a time' };
  const out = [];
  const seen = new Set();   // one row per key per request: an upsert may not touch the same row twice
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const code = txt(r.prod_code, 60);
    if (!code) continue;
    if (kind === 'products') {
      out.push({
        country: c, prod_code: code,
        description: txt(r.description, 200), class_store: txt(r.class_store, 30),
        brand: txt(r.brand, 80), sub_brand: txt(r.sub_brand, 80), main_group: txt(r.main_group, 80),
        sub_group: txt(r.sub_group, 80), sub_segment: txt(r.sub_segment, 80),
        selling_price: numOrNull(r.selling_price), avg_cost: numOrNull(r.avg_cost), wh_soh: numOrNull(r.wh_soh), stores_soh: numOrNull(r.stores_soh),
        created_on: dateOk(r.created_on), last_sold: dateOk(r.last_sold),
        updated_at: new Date().toISOString(),
      });
    } else if (kind === 'stock' || kind === 'store_stock') {
      const branch = txt(r.branch, 120);
      if (!branch) continue;
      const o = { country: c, prod_code: code, branch, store_soh: numOrNull(r.store_soh), as_of: dateOk(r.as_of) };
      // v136.20: a store's own price, what it has on order and the LPO date (v241-0), only when sent
      if (kind === 'store_stock') {
        if (has(r, 'store_price')) o.store_price = numOrNull(r.store_price);
        if (has(r, 'on_order')) o.on_order = numOrNull(r.on_order);
        if (has(r, 'on_order_date')) o.on_order_date = dateOk(r.on_order_date);
      }
      out.push(o);
    } else if (kind === 'promo') {
      // v136.20: the active promotions file (v241-0), one row per branch per product
      const branch = txt(r.branch, 120);
      if (!branch) continue;
      out.push({
        country: c, prod_code: code, branch,
        promo_name: txt(r.promo_name, 120), reason: txt(r.reason, 80), price_mode: txt(r.price_mode, 30),
        start_date: dateOk(r.start_date), end_date: dateOk(r.end_date),
        std_price: numOrNull(r.std_price), disc_amt: numOrNull(r.disc_amt), disc_pct: numOrNull(r.disc_pct), promo_price: numOrNull(r.promo_price),
        as_of: dateOk(r.as_of),
      });
    } else if (kind === 'daily') {
      const branch = txt(r.branch, 120);
      const day = dateOk(r.sale_date);
      if (!branch || !day) continue;
      out.push({ country: c, prod_code: code, branch, sale_date: day, qty: numOrNull(r.qty) || 0, sales: numOrNull(r.sales) || 0, cost: numOrNull(r.cost) || 0, discount: numOrNull(r.discount) || 0 });
    } else if (kind === 'catalogue') {
      // the product details file: its own columns of scan_products only. v136.20: and only the columns the
      // file HAS - a field missing from the row is left out, never written as empty (the new master data file has
      // no prices, costs or store class, and must not clear them); see the uniform-keys step below
      const o = { country: c, prod_code: code, description: txt(r.description, 200) };
      CATALOGUE_OPTIONAL.forEach(([k, f]) => { if (has(r, k)) o[k] = f(r[k]); });
      o.updated_at = new Date().toISOString();
      out.push(o);
    } else if (kind === 'prices') {
      // the price matrix: the current price, and which branch it was read from
      out.push({ country: c, prod_code: code, shelf_price: numOrNull(r.shelf_price), shelf_price_branch: txt(r.shelf_price_branch, 120) });
    } else if (kind === 'warehouse') {
      // v136.16: the Falcon warehouse stock file: wh_soh only
      out.push({ country: c, prod_code: code, wh_soh: numOrNull(r.wh_soh) });
    } else if (kind === 'barcodes') {
      const barcode = normBarcode(r.barcode);
      if (!barcode) continue;
      out.push({ country: c, barcode, prod_code: code, source: r.source === 'linked' ? 'linked' : 'main' });
    }
  }
  const keyOf = { barcodes: (o) => o.barcode, daily: (o) => o.prod_code + '|' + o.branch + '|' + o.sale_date, stock: (o) => o.prod_code + '|' + o.branch, store_stock: (o) => o.prod_code + '|' + o.branch, promo: (o) => o.prod_code + '|' + o.branch };
  const k = keyOf[kind] || ((o) => o.prod_code);
  const unique = out.filter((o) => { const key = k(o); if (seen.has(key)) return false; seen.add(key); return true; });
  // A bulk upsert needs the same keys on every row: a key some rows carry and others do not is given to
  // all (null where it was missing). The page sends every row of a file with the same keys, so in practice
  // this never adds anything; it only stops a mixed request from failing.
  const keys = new Set();
  unique.forEach((o) => Object.keys(o).forEach((x) => keys.add(x)));
  unique.forEach((o) => keys.forEach((x) => { if (!has(o, x)) o[x] = null; }));
  return { ok: true, rows: unique };
}

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k) && o[k] !== undefined;
// The catalogue's fields other than the code and the description, each written only when the row has it.
const CATALOGUE_OPTIONAL = [
  ['class_store', (v) => txt(v, 30)], ['brand', (v) => txt(v, 80)], ['sub_brand', (v) => txt(v, 80)],
  ['main_group', (v) => txt(v, 80)], ['sub_group', (v) => txt(v, 80)], ['sub_segment', (v) => txt(v, 80)],
  ['status', (v) => txt(v, 30)], ['standard_price', numOrNull], ['standard_cost', numOrNull],
  ['created_on', dateOk],   // v136.20: the master data's "Launch Date"
];

// v136.20: the scan_stock columns v241-0 adds. Until it runs, a store stock upload that sends them is
// retried without them (src/worker/routes/scan.js), so the stock itself still loads.
export const SCAN_STOCK_V241_FIELDS = ['store_price', 'on_order', 'on_order_date'];
export function withoutFields(rows, fields) {
  return (rows || []).map((r) => { const o = Object.assign({}, r); fields.forEach((f) => { delete o[f]; }); return o; });
}
// A PostgREST answer that says a column (or the table) is not there: PGRST204 / 42703 on a column,
// PGRST205 / 42P01 on a table.
export function isMissingSchema(message, names) {
  const m = String(message || '');
  if (!/PGRST204|PGRST205|42703|42P01|does not exist|schema cache|Could not find/i.test(m)) return false;
  return !names || !names.length || names.some((x) => m.indexOf(x) !== -1);
}

// v136.70 (a Qatar upload, 27 Oct 2026: "can we add label due to discount price '0'"). A deal with no single price
// (Buy2 Get1, 4 for 149) comes in the file with Discounted Selling Price 0 or blank. That 0 is not a price: it is
// never shown, never in a price range and never a label to check against. The deal is named by its Discount
// Reason (Code.Discount Reason), else the promotion's name.
export function promoPrice(r) {
  const p = firstNum(r && r.promo_price);
  return p != null && p > 0 ? p : null;
}

// v136.20: one store's active promotion prices -> Map(normCode(prod_code) -> promo_price), for the bay's label
// check. Same rule as promoSummary: ended (before today, Dubai) or not yet started rows are left out.
export function activePromoPrices(rows, today) {
  const t = String(today || '').slice(0, 10);
  const out = new Map();
  (rows || []).forEach((r) => {
    if (!r || !r.prod_code) return;
    if (t && r.end_date && String(r.end_date).slice(0, 10) < t) return;
    if (t && r.start_date && String(r.start_date).slice(0, 10) > t) return;
    const p = promoPrice(r);   // v136.70: a deal (price 0) has no promotion price for a label to match
    if (p != null) out.set(normCode(r.prod_code), p);
  });
  return out;
}

// v136.20: what a product's promotions say. rows: scan_promos rows for one product (every branch). A row
// whose end date is before today (Dubai), or whose start is after it, is not shown. branch: the viewer's
// branch as the files spell it (or null). -> { here, stores, min_price, max_price, ends } or null.
export function promoSummary(rows, branch, today) {
  const t = String(today || '').slice(0, 10);
  const live = (rows || []).filter((r) => r && r.branch && (!r.end_date || !t || String(r.end_date).slice(0, 10) >= t) && (!r.start_date || !t || String(r.start_date).slice(0, 10) <= t));
  if (!live.length) return null;
  const prices = live.map(promoPrice).filter((v) => v != null);   // v136.70: a deal's 0 is not a price
  const pick = (r) => r ? {
    branch: r.branch, name: r.promo_name || null, reason: r.reason || null,
    price: promoPrice(r), was: firstNum(r.std_price), disc_pct: firstNum(r.disc_pct),
    start: r.start_date ? String(r.start_date).slice(0, 10) : null, end: r.end_date ? String(r.end_date).slice(0, 10) : null,
  } : null;
  // v136.70: the deals on offer, by their Discount Reason (at most three, as written in the file)
  const deals = [];
  live.forEach((r) => { const d = String(r.reason || '').trim(); if (d && deals.indexOf(d) === -1 && deals.length < 3) deals.push(d); });
  return {
    here: branch ? pick(live.find((r) => r.branch === branch)) : null,
    stores: new Set(live.map((r) => r.branch)).size,
    min_price: prices.length ? Math.min(...prices) : null,
    max_price: prices.length ? Math.max(...prices) : null,
    deals,
  };
}

export const SCAN_CONFLICT = {
  products: 'org_id,country,prod_code',
  catalogue: 'org_id,country,prod_code',
  prices: 'org_id,country,prod_code',
  barcodes: 'org_id,country,barcode',
  stock: 'org_id,country,prod_code,branch',
  store_stock: 'org_id,country,prod_code,branch',
  warehouse: 'org_id,country,prod_code',
  daily: 'org_id,country,prod_code,branch,sale_date',
  promo: 'org_id,country,branch,prod_code',
};
export const SCAN_TABLE = { products: 'scan_products', stock: 'scan_stock', daily: 'scan_daily', catalogue: 'scan_products', prices: 'scan_products', barcodes: 'scan_barcodes', store_stock: 'scan_stock', warehouse: 'scan_products', promo: 'scan_promos' };
// The kinds that belong to a DSR sales upload (with an upload_id and a period) and those that do not.
export const SCAN_SALES_KINDS = ['products', 'stock', 'daily'];
// v136.16: the stock files (Cubes store stock, Falcon warehouse stock) load like the catalogue: no period.
// v136.20: so do the active promotions (promo).
export const SCAN_CATALOGUE_KINDS = ['catalogue', 'prices', 'barcodes', 'store_stock', 'warehouse', 'promo'];

// The viewer's branch as the FILE spells it, from their store's name (stores.name) and its
// upload_name alias - the same lookup the Top 50 uses (findTop50StoreKey). Pure: branches are the
// branch names present in the data for this country.
export function branchForStore(storeName, aliasMap, branches, findKey) {
  if (!storeName || !Array.isArray(branches) || !branches.length) return null;
  const hit = findKey(storeName, aliasMap, branches);
  if (hit) return hit;
  // "TE - ABU DHABI - REEM MALL": the Top 50 normaliser strips "ELC -" only.
  const want = normText(storeName);
  for (const b of branches) {
    const plain = normText(String(b).replace(/^\s*(ELC|TE)\s*-\s*/i, ''));
    if (plain === want) return b;
  }
  return null;
}
